import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import { ILoadingInstance } from 'element-plus/lib/el-loading/src/loading.type'
import { IDataType } from '@/service/common/commonType'
import qs from 'qs'
import store from '@/store'
import {
  finishRequestDiagnostic,
  reportDiagnostic,
  reportRequestFailure,
  reportResponseStructureSuspect,
  startRequestDiagnostic,
  summarizeResponseShape
} from '@/utils/diagnostics'
import type { RequestDiagnosticContext } from '@/utils/diagnostics'

// 扩展 Window 接口以添加自定义属性
declare global {
  interface Window {
    _isReloginShown?: boolean
  }
}

interface LfRequestInterceptors {
  requestInterceptor?: (config: AxiosRequestConfig) => AxiosRequestConfig
  requestInterceptorCatch?: (error: any) => any
  responseInterceptor?: <T = AxiosResponse>(res: T) => T
  responseInterceptorCatch?: (error: any) => any
}

interface LfRequestConfig extends AxiosRequestConfig {
  interceptors?: LfRequestInterceptors
  showLoading?: boolean
  __diagnosticContext?: RequestDiagnosticContext
}

const DEFAULT_LOADING = false

const getDiagnosticContext = (config?: AxiosRequestConfig) => {
  return (config as LfRequestConfig | undefined)?.__diagnosticContext
}

const hasPagination = (config: AxiosRequestConfig) => {
  const requestData =
    config.method?.toLowerCase() === 'get' ? config.params : config.data
  const hasPageFields =
    requestData &&
    typeof requestData === 'object' &&
    ('pageNo' in requestData || 'pageSize' in requestData)

  return Boolean(
    hasPageFields || config.url?.toLowerCase().includes('querybycondition')
  )
}

const getPayloadForDiagnostics = (data: unknown) => {
  if (!data || data instanceof Blob || typeof data !== 'object') return data
  const value = data as Record<string, unknown>
  if (
    Object.prototype.hasOwnProperty.call(value, 'data') &&
    (Object.prototype.hasOwnProperty.call(value, 'code') ||
      Object.prototype.hasOwnProperty.call(value, 'message'))
  ) {
    return value.data
  }
  return data
}

class LfRequest {
  instance: AxiosInstance
  interceptors?: LfRequestInterceptors
  loading?: ILoadingInstance
  showLoading?: boolean

  constructor(config: LfRequestConfig) {
    this.instance = axios.create(config)
    this.showLoading = config.showLoading ?? DEFAULT_LOADING

    this.interceptors = config.interceptors

    this.instance.interceptors.request.use(
      this.interceptors?.requestInterceptor,
      this.interceptors?.requestInterceptorCatch
    )

    this.instance.interceptors.response.use(
      this.interceptors?.responseInterceptor,
      this.interceptors?.responseInterceptorCatch
    )

    this.instance.interceptors.request.use(
      (config) => {
        const requestConfig = config as LfRequestConfig
        requestConfig.__diagnosticContext = startRequestDiagnostic({
          method: config.method,
          url: config.url,
          expectsPage: hasPagination(config)
        })

        if (config.method === 'get') {
          //如果是get请求，且params是数组类型如arr=[1,2]，则转换成arr=1&arr=2
          config.paramsSerializer = function (params) {
            return qs.stringify(params, { arrayFormat: 'repeat' })
          }
        }
        if (config.headers) {
          config.headers.srcUrl = window.location.hash
        } else {
          config.headers = {
            srcUrl: window.location.hash
          }
        }

        if (this.showLoading) {
          this.loading = ElLoading.service({
            lock: true,
            text: '正在请求数据...',
            background: 'rgba(0, 0, 0, 0.5)'
          })
        }
        return config
      },
      (error) => {
        reportDiagnostic('error', 'request', 'request-config-error', {
          name: error?.name,
          message: error?.message || 'Request config error'
        })
        return error
      }
    )

    this.instance.interceptors.response.use(
      (res: AxiosResponse<IDataType<any>>) => {
        if (axios.isAxiosError(res as any)) {
          const swallowedError = res as any
          reportRequestFailure(
            getDiagnosticContext(swallowedError.config),
            swallowedError,
            'http-error'
          )
          this.loading?.close()
          return swallowedError.data
        }

        const data = res.data
        const context = getDiagnosticContext(res.config)
        const responseSummary = summarizeResponseShape(
          getPayloadForDiagnostics(data)
        )
        this.loading?.close()
        if (data instanceof Blob) {
          finishRequestDiagnostic(context, 'success', {
            httpStatus: res.status,
            ...responseSummary
          })
          return data
        }
        if (!data || !data.code) {
          finishRequestDiagnostic(context, 'success', {
            httpStatus: res.status,
            ...responseSummary
          })
          reportResponseStructureSuspect(context, responseSummary)
          return data
        }
        if (data.code !== 200) {
          finishRequestDiagnostic(context, 'business-error', {
            httpStatus: res.status,
            businessCode: data.code,
            responseShape: responseSummary.shape
          })
          ElMessage.error({
            message: data.message || '请求错误',
            type: 'error',
            duration: 3 * 1000
          })
          if (data.code === 401) {
            // to re-login
            // 防止重复弹窗
            if (window._isReloginShown) {
              return Promise.reject(new Error(data.message || '请求错误'))
            }
            window._isReloginShown = true
            ElMessageBox.confirm('登录已失效，是否重新登录', '确认登出', {
              confirmButtonText: '重新登录',
              cancelButtonText: '取消',
              type: 'warning',
              closeOnClickModal: false,
              closeOnPressEscape: false
            })
              .then(() => {
                store.dispatch('login/resetToken').then(() => {
                  location.reload()
                })
              })
              .catch(() => {
                // 用户取消，不处理
              })
              .finally(() => {
                window._isReloginShown = false
              })
          }
          return Promise.reject(new Error(data.message || '请求错误'))
        }

        finishRequestDiagnostic(context, 'success', {
          httpStatus: res.status,
          businessCode: data.code,
          ...responseSummary
        })
        reportResponseStructureSuspect(context, responseSummary)
        return data.data
      },
      (error) => {
        reportRequestFailure(
          getDiagnosticContext(error?.config),
          error,
          error?.response ? 'http-error' : 'request-error'
        )
        this.loading?.close()
        // 处理HTTP错误状态码（如网络错误、401等）
        if (error.response && error.response.status === 401) {
          // 防止重复弹窗
          if (!window._isReloginShown) {
            window._isReloginShown = true
            ElMessageBox.confirm('登录已失效，是否重新登录', '确认登出', {
              confirmButtonText: '重新登录',
              cancelButtonText: '取消',
              type: 'warning',
              closeOnClickModal: false,
              closeOnPressEscape: false
            })
              .then(() => {
                store.dispatch('login/resetToken').then(() => {
                  location.reload()
                })
              })
              .catch(() => {
                // 用户取消，不处理
              })
              .finally(() => {
                window._isReloginShown = false
              })
          }
        } else {
          ElMessage.error({
            message: error.message || '网络错误',
            type: 'error',
            duration: 3 * 1000
          })
        }
        return Promise.reject(error)
      }
    )
  }

  request<T>(config: LfRequestConfig): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (config.interceptors?.requestInterceptor) {
        config = config.interceptors.requestInterceptor(config)
      }

      if (config.showLoading !== undefined) {
        this.showLoading = config.showLoading
      }

      this.instance
        .request<any, T>(config)
        .then((res) => {
          if (config.interceptors?.responseInterceptor) {
            res = config.interceptors.responseInterceptor(res)
          }
          this.showLoading = DEFAULT_LOADING
          resolve(res)
        })
        .catch((err) => {
          this.showLoading = DEFAULT_LOADING
          reject(err)
        })
    })
  }

  get<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET' })
  }

  post<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'POST' })
  }

  delete<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE' })
  }

  put<T>(config: LfRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT' })
  }
}

export default LfRequest
